import type { UIComponent } from '@gik/core/types/UI';
import { useBemCN } from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import DotsHorizontalIcon from '@heroicons/react/outline/DotsHorizontalIcon';
import PhotographIcon from '@heroicons/react/outline/PhotographIcon';
import he from 'he';
import React from 'react';
import type { MentionItem, OnChangeHandlerFunc, SuggestionDataItem } from 'react-mentions';
import { InputMentions } from '../../../ui/Mentions';
import { Popover } from '../../../ui/Popover';
import { PopoverMenu, PopoverMenuItem } from '../../../ui/PopoverMenu';
import { SvgIcon } from '../../../ui/SvgIcon';

export type commentInputVariant = 'neutral';

export type CommentInputProps = {
  value?: string;
  forId?: string;
  isLoading?: boolean;
  autoFocus?: boolean;
  hasImageUpload?: boolean;
  hasMore?: boolean;
  disabled?: boolean;
  submitButton?: boolean;
  submitOnEnter?: boolean;
  cancelOnEscape?: boolean;
  placeholder?: string;
  variant?: commentInputVariant;
  users?: SuggestionDataItem[];
  onChange?: OnChangeHandlerFunc;
  onCancel?: () => void;
  onSubmit?: (forId: string, value: string) => void;
  onSuccess?: (forId: string, value: string) => void;
} & UIComponent;

function CommentInputComp({
  className,
  autoFocus,
  isLoading,
  hasImageUpload,
  hasMore,
  value,
  disabled,
  variant,
  submitButton,
  submitOnEnter = true,
  cancelOnEscape = true,
  onChange,
  onCancel,
  onSubmit,
  onSuccess,
  users,
  forId,
  ...otherProps
}: CommentInputProps): React.ReactElement {
  const bem = useBemCN('comment-input');
  const [error, setError] = React.useState<string>();

  const handleSubmit = React.useCallback(
    async (value: string) => {
      if (!value?.trim()) return;

      setError(null);
      onChange?.({ target: { value: '' } }, '', '', []);
      onSubmit?.(forId, value);
    },
    [forId, onChange, onSubmit]
  );

  const handleChange = React.useCallback(
    (event: { target: { value: string } }, newValue: string, newPlainTextValue: string, mentions: MentionItem[]) => {
      const maxLength = 1000;

      const newValueSanitized = newValue.trim();
      if (newValueSanitized.length > maxLength) {
        setError(`Cannot be more than ${maxLength} characters`);
      }

      onChange(event, newValueSanitized, newPlainTextValue, mentions);
    },
    [onChange]
  );

  return (
    <div {...bem(null, [{ [`variant-${variant}`]: variant }], className)}>
      <InputMentions
        disabled={isLoading || disabled}
        value={value ? he.decode(value) : value}
        onChange={handleChange}
        users={users}
        cancelOnEscape={cancelOnEscape}
        submitOnEnter={submitOnEnter}
        padding
        id={`${forId}-input`}
        autoFocus={autoFocus}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        {...otherProps}
      />

      {error && <div {...bem('error')}>{error}</div>}
      {hasImageUpload && (
        <Popover
          trigger={
            <div {...bem('action-icon-wrapper')}>
              <div {...bem('action-icon')}>
                <SvgIcon Icon={PhotographIcon} />
              </div>
            </div>
          }
        >
          TODO
        </Popover>
      )}
      {hasMore && (
        <Popover
          noPad
          trigger={
            <div {...bem('action-icon-wrapper')}>
              <div {...bem('action-icon')}>
                <SvgIcon Icon={DotsHorizontalIcon} />
              </div>
            </div>
          }
        >
          <PopoverMenu>
            <PopoverMenuItem>Click Me</PopoverMenuItem>
            <PopoverMenuItem>Do Something</PopoverMenuItem>
          </PopoverMenu>
        </Popover>
      )}
    </div>
  );
}

export const CommentInput = withComponentErrorBoundary(CommentInputComp);
